import React, { useMemo } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../../components/ui/card';
import { useAnalyticsContext } from '../analytics-context';

const DEVICE_COLORS = ['#2563eb', '#10b981', '#f97316', '#9333ea'];

const GaDeviceChart: React.FC = () => {
  const { ga, isCustomRangePending } = useAnalyticsContext();
  const deviceError = ga.demographics.error as any;

  const chartData = useMemo(() => {
    return ga.demographics.devices
      .map((device, index) => ({
        name: device.device,
        value: device.users,
        color: DEVICE_COLORS[index % DEVICE_COLORS.length],
      }))
      .filter(item => item.value > 0);
  }, [ga.demographics.devices]);

  if (!ga.integration.isConfigured) {
    return (
      <Card className="border-none shadow-sm h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">
            Device Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent className="flex h-[280px] items-center justify-center text-sm text-muted-foreground">
          {ga.integration.message ||
            'Connect Google Analytics to view device breakdown.'}
        </CardContent>
      </Card>
    );
  }

  if (deviceError && deviceError.status && deviceError.status !== 400) {
    return (
      <Card className="border-none shadow-sm h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">
            Device Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent className="flex h-[280px] items-center justify-center text-sm text-red-600">
          {deviceError.data?.message ||
            deviceError.message ||
            'Failed to load device breakdown.'}
        </CardContent>
      </Card>
    );
  }

  if (ga.demographics.isLoading) {
    return (
      <Card className="border-none shadow-sm h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">
            Device Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent className="flex h-[280px] items-center justify-center">
          <div className="h-48 w-48 animate-pulse rounded-full bg-muted/50" />
        </CardContent>
      </Card>
    );
  }

  if (isCustomRangePending) {
    return (
      <Card className="border-none shadow-sm h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">
            Device Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent className="flex h-[280px] items-center justify-center text-sm text-muted-foreground">
          Select a start and end date to view Google Analytics insights.
        </CardContent>
      </Card>
    );
  }

  if (!chartData.length) {
    return (
      <Card className="border-none shadow-sm h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">
            Device Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent className="flex h-[280px] items-center justify-center text-sm text-muted-foreground">
          No device data available for this period.
        </CardContent>
      </Card>
    );
  }

  const totalUsers = chartData.reduce((sum, item) => sum + item.value, 0);

  return (
    <Card className="border-none shadow-sm h-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-semibold">
          Device Breakdown
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="h-[220px]">
          <ResponsiveContainer>
            <PieChart>
              <Tooltip
                formatter={(value: number, name: string) => [
                  value.toLocaleString(),
                  name,
                ]}
              />
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="name"
                innerRadius={60}
                outerRadius={90}
                paddingAngle={3}
              >
                {chartData.map(entry => (
                  <Cell key={entry.name} fill={entry.color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="grid gap-2 text-sm">
          {chartData.map(item => {
            const pct = totalUsers > 0 ? (item.value / totalUsers) * 100 : 0;
            return (
              <div
                key={item.name}
                className="flex items-center justify-between rounded-lg border bg-card/50 px-3 py-2"
              >
                <div className="flex items-center gap-2">
                  <span
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="font-medium text-foreground">
                    {item.name}
                  </span>
                </div>
                <span className="text-muted-foreground">
                  {item.value.toLocaleString()} · {pct.toFixed(1)}%
                </span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default GaDeviceChart;

